/**
 * Shared HTTP read-API contract shapes for the customer-api `traveler` surface
 * (API-04 / D-US-01). Imported by the cross-workstream `multichannel-auth`
 * consumer so it parses the SAME Zod types the customer-api emits, instead of
 * re-declaring them.
 *
 * DISTINCT from the auth `user` entity (`packages/domain/src/entities/user/`)
 * — these are `Traveler*`/`*View` names with PLAIN `z.string()` ids. The
 * service-local branded `UserId` (which lives in `apps/services/customer`) is
 * deliberately NOT leaked into the shared contract: `multichannel-auth` must
 * not depend on it.
 *
 * Field names are DOMAIN terms only (`name`/`phone`/`companyId`) — never the
 * Portuguese source/production names. The schemas are the wire strip seam
 * (ACL-01): a `.parse(...)` drops unknown keys, so a source token present on
 * the input never reaches the wire (proven by the co-located strip tests).
 *
 * The field sets mirror the service-local entities in
 * `apps/services/customer/src/domain/entities/` (User / Voucher / Company),
 * brand-stripped.
 */
import { z } from 'zod';
import { BaggageAllowanceSchema } from '../../flight/baggage_allowance';
import { FareRuleSchema } from '../../flight/fare_rules';
import { AgentKindSchema } from '../agent/agent';
import { ApprovalRequestContextSchema } from './approval_verdict';
import { ExpenseKmManualChangeSchema, ExpenseKmTrackedSchema } from './expense_km';
import { MobilityCarDriverSchema } from './mobility_car_driver';
import { VoucherPolicySchema, VoucherQuotationSchema } from './voucher_request';
import { VoucherCancellationSchema } from './voucher_canceled';

export const TravelerViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.email().nullable(),
  phone: z.string().nullable(),
  companyId: z.string().nullable(),
  createdAt: z.coerce.date(),
});
export type TravelerView = z.infer<typeof TravelerViewSchema>;

// Brand-free mirror of the service-local `CustomerSchema` cost-center ref shapes.
const CostCenterRefView = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  externalCode: z.string().optional(),
});
const CompanyCostCenterView = z.object({
  company: CostCenterRefView.optional(),
  costCenter: CostCenterRefView.optional(),
});

/**
 * Rich operational travel profile for `GET /internal/customers/:id` (EXP-02).
 *
 * Plain `z.object` (default STRIP mode) — the brand-free mirror of the
 * service-local `CustomerSchema` operational fields, consumed on-demand by the
 * agents booking tools. All profile fields are `.optional()` (foreign data is
 * partial), matching `TravelerViewSchema`'s brand-free `z.string()` ids.
 *
 * Raw Tier-3 sensitive documents (`document` = CPF, `documents` = RG/passport/DNI)
 * are INTENTIONALLY ABSENT — the absence IS the LGPD gate
 * (D-CE-06 / SENS-01); their gated exposure is Phase 5 only, never this view.
 * As formas MASCARADAS e os metadados de validade (documentMasked/documentsMeta/hasNoCPF)
 * são a projeção não-sensível sancionada (F1 perfil); os valores crus seguem exclusivos do T3.
 */
export const CustomerProfileViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.email().nullable(),
  phone: z.string().nullable(),
  companyId: z.string().nullable(),
  createdAt: z.coerce.date(),
  // CUST-02 — operational travel profile (raw document/documents deliberately omitted).
  birthdate: z.coerce.date().nullable().optional(),
  gender: z.string().nullable().optional(),
  // `code` (ISO alpha-2) is what the profile screen's nationality select keys
  // on — `id` is the legacy's numeric catalog id, kept only as a mirror residue.
  nationality: z
    .object({ id: z.string().optional(), code: z.string().optional(), name: z.string().optional() })
    .nullable()
    .optional(),
  boardingName: z
    .object({ firstName: z.string().optional(), lastName: z.string().optional() })
    .nullable()
    .optional(),
  // `.nullable()` (F4/F5 profile editing): customer-api's classe-A merge folds a
  // cleared override (`null`) to `undefined` — the key simply drops off the wire —
  // but the BFF fixture mode applies the patch in memory and carries the
  // traveler's explicit `null` through, so the view must represent it without the
  // response `.parse()` throwing.
  preferrerName: z.string().nullable().optional(),
  nameCivil: z.string().optional(),
  tsaPreCheck: z.string().nullable().optional(),
  countryCode: z.string().optional(),
  // F1 perfil — projeções NÃO-sensíveis do tier-3, computadas no view-assembly
  // do customer-api (document_masking.ts). Os valores CRUS `document`/`documents`
  // continuam intencionalmente ausentes: o strip segue sendo o gate (SENS-01).
  hasNoCPF: z.boolean().optional(),
  documentMasked: z.string().nullable().optional(),
  documentsMeta: z
    .object({
      rgMasked: z.string().nullable().optional(),
      dniMasked: z.string().nullable().optional(),
      passportMasked: z.string().nullable().optional(),
      passportCountry: z.string().optional(),
      passportValidUntil: z.coerce.date().nullable().optional(),
      passportIssuedAt: z.coerce.date().nullable().optional(),
    })
    .nullable()
    .optional(),
  visas: z
    .array(
      z.object({
        code: z.string().optional(),
        country: z.string().optional(),
        validUntil: z.coerce.date().nullable().optional(),
      }),
    )
    .optional(),
  vaccines: z
    .array(
      z.object({
        name: z.string().optional(),
        date: z.coerce.date().nullable().optional(),
      }),
    )
    .optional(),
  emergencyContacts: z
    .array(
      z.object({
        name: z.string().optional(),
        countryCode: z.string().optional(),
        phone: z.string().optional(),
      }),
    )
    .optional(),
  // CUST-03 — deterministic preferences.
  mileagePlans: z
    .array(
      z.object({
        airCompany: z.string().optional(),
        airCode: z.string().optional(),
        planCode: z.string().optional(),
        firstName: z.string().optional(),
        lastName: z.string().optional(),
      }),
    )
    .optional(),
  costCenterDefault: CompanyCostCenterView.nullable().optional(),
  userCostCenters: z
    .object({
      allActive: z.boolean().optional(),
      costCenters: z.array(CompanyCostCenterView).nullable().optional(),
    })
    .nullable()
    .optional(),
  // Foto do avatar (#2640). NÃO é campo do legado: vive no store próprio do
  // customer-api (`customer_photos` + S3 privado) e chega aqui como URL
  // pré-assinada de vida curta — o consumidor a usa em `<img src>` e cai nas
  // iniciais quando ela expira. `null` = sem foto; ausente = servidor antigo.
  // Fora do `TravelerProfilePatchSchema`: a foto troca pelas rotas `/photo`.
  photoUrl: z.url().nullable().optional(),
});
export type CustomerProfileView = z.infer<typeof CustomerProfileViewSchema>;

/**
 * Tier-3 GATED documents view for `GET /internal/customers/:id/documents` (D-P5-01,
 * SENS-02). Brand-free `z.object` (default STRIP) carrying ONLY the sensitive document
 * fields + the `id` spine — NO operational profile fields. The strip IS the
 * minimal-disclosure boundary that complements `CustomerProfileViewSchema`'s SENS-01
 * omission: the rich profile never carries documents, and this view never carries the
 * rich profile. Served only behind the purpose-gated + audited route (LGPD).
 */
/**
 * Who is reading tier-3 documents through the gated route (`x-actor-kind`), kept
 * on every audit row. Extends the assignable `AgentKind` (`agent` = AI agent,
 * `operator` = human operator) with `customer`: the traveler reading their OWN
 * documents ("Revelar dados" on the profile screen, #2638). Not `AgentKind`
 * itself — nobody assigns work to the customer — and not `MessageAuthor`:
 * `system` never reads documents. The BFF that relays the traveler MUST send
 * `customer` with the traveler's own id; relabeling that access as an operator
 * would falsify the LGPD trail.
 */
export const DocumentAccessActorKindSchema = z.enum([...AgentKindSchema.options, 'customer']);
export type DocumentAccessActorKind = z.infer<typeof DocumentAccessActorKindSchema>;

export const CustomerDocumentsViewSchema = z.object({
  id: z.string(),
  document: z.string().nullable().optional(), // CPF
  hasNoCPF: z.boolean().optional(),
  documents: z
    .object({
      rg: z.string().nullable().optional(),
      dni: z.string().optional(),
      passport: z.string().optional(),
      country: z.string().optional(),
      validate: z.coerce.date().nullable().optional(),
      dateEmited: z.coerce.date().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type CustomerDocumentsView = z.infer<typeof CustomerDocumentsViewSchema>;

/**
 * Cross-modal travel summary for a voucher (`null` for non-travel modals like
 * PAYMENT). Mirrors the service-local `VoucherJourney`. The spine a future
 * `Trip` view groups on; rich enough for a support agent/AI to understand the
 * journey, without the giant source document.
 */
export const VoucherJourneyViewSchema = z.object({
  origin: z.string().nullable(),
  destination: z.string().nullable(),
  startAt: z.coerce.date().nullable(),
  endAt: z.coerce.date().nullable(),
  // Display wall-clock at the journey endpoints, offset-less ISO
  // (`YYYY-MM-DDTHH:mm:ss`). A FLIGHT takes both ends from its segments; any
  // other spine is read like a segment (`flightSourceTimes`): the source writes
  // this clock framed at Brazil's -03:00, and the true instant `startAt`/`endAt`
  // above is derived from it. Null when the origin/destination is not an
  // IATA-coded airport (a hotel's street address) — the instant is then raw.
  startLocal: z.string().nullable().optional(),
  endLocal: z.string().nullable().optional(),
});
export type VoucherJourneyView = z.infer<typeof VoucherJourneyViewSchema>;

/**
 * Canonical, traveler-facing voucher status. The product's internal state
 * machine has more states — REQUESTED, WAITING_FOR_QUOTATION, AWAITING_APPROVAL,
 * WAITING_FOR_EMISSION, EMITTED, CANCELED, EXPIRED, REPROVED, REQUEST_DENIED — an
 * operational vocabulary that means nothing to the traveler. The ACL maps those
 * raw `statusName` values into these buckets (customer-api voucher mapper); the
 * raw value is preserved separately in `VoucherView.internalStatus`.
 */
export const VoucherStatusSchema = z.enum([
  'IN_PROGRESS', // requested / quoting / awaiting issuance — being arranged
  'AWAITING_APPROVAL', // needs an approver's decision before it proceeds
  'CONFIRMED', // emitted — ready to travel
  'CANCELLED', // cancelled after request
  'EXPIRED', // request lapsed
  'DECLINED', // approval reproved or quote refused
]);
export type VoucherStatus = z.infer<typeof VoucherStatusSchema>;

/**
 * Per-modal operational detail — what a traveler needs to actually travel,
 * which differs by modal. Discriminated on `kind` so each modal's fields are
 * typed (a flight has segments/PNR/seat; a hotel has address/check-in/room).
 * CONCIERGE_* (offline-arranged) vouchers normalize to their base `kind` —
 * a concierge flight is still a flight to the traveler. PAYMENT carries the wallet
 * spend; refunds (EXPENSE, EXPENSE_KM) carry the expense they pay back. `null` for
 * travel extras (seat/luggage purchases). Curated from the product's per-modal view (voucher-api
 * `find.voucher.info.details.js`); source names never appear here (ACL-01) —
 * the customer-api voucher mapper translates them.
 *
 * Most fields are nullable: the source doc enriches as it moves through the
 * state machine, so detail is only complete once the voucher is CONFIRMED.
 */
/**
 * Live operational state of ONE flight leg, as the airline is reporting it right
 * now — gate, terminal, delay, status.
 *
 * THE ONE FIELD HERE THAT IS NOT THE VOUCHER. Everything else on
 * {@link FlightSegmentSchema} is the booking as the product sold it; this is the
 * flight as it is actually operating, and it comes from a different producer
 * (the Cirium integration projects it into a customer-api collection, which
 * attaches it on the voucher read). Naming the provenance matters because the
 * two disagree on purpose: the voucher says the flight departs 08:10 from
 * wherever it was scheduled; this says it now departs 08:35 from gate 217.
 *
 * Every field is nullable and the whole block is optional, because coverage is
 * partial in three independent ways: not every voucher is monitored, not every
 * monitored leg has been observed yet, and airport resource reporting varies by
 * airport. A null is ALWAYS "not reported" and never "changed to nothing" — a
 * consumer must not render an absent gate as a cleared gate.
 *
 * `observedAt` is not decoration: it is the only thing that makes the rest
 * safe to show. A gate observed six hours ago sends a traveler to the wrong end
 * of the airport, so a consumer is expected to bound freshness itself — this
 * contract carries the timestamp and deliberately does not decide the window,
 * because the producer does not know the surface.
 */
export const FlightLiveStatusSchema = z.object({
  /** Provider flight status: S scheduled · A active · L landed · C cancelled · D diverted · R redirected. */
  status: z.string().nullable(),
  departureGate: z.string().nullable(),
  departureTerminal: z.string().nullable(),
  arrivalGate: z.string().nullable(),
  arrivalTerminal: z.string().nullable(),
  baggageClaim: z.string().nullable(),
  /** Minutes late. Absent is NOT zero — zero would claim the flight is on time. */
  departureDelayMinutes: z.number().nullable(),
  arrivalDelayMinutes: z.number().nullable(),
  /** Provider-native UTC ISO instants. */
  estimatedGateDeparture: z.string().nullable(),
  estimatedGateArrival: z.string().nullable(),
  /**
   * The schedule the airline holds NOW (UTC ISO), which moves when it reschedules the flight
   * (#6002). The voucher keeps the time it was sold at — registration only accepts a leg whose
   * schedule matches it — so a difference between the two IS the reschedule. A delay never moves
   * these; it moves the estimates above. Optional: rows written before the field still parse.
   */
  scheduledGateDeparture: z.string().nullable().optional(),
  scheduledGateArrival: z.string().nullable().optional(),
  /**
   * The departure gate before the current one changed it (#6003): the last DIFFERENT gate in the
   * leg's recorded history, so a gate that disappears and comes back the same is no change — the
   * live-activity rule (#4369). `null` = no change. Optional: rows written before still parse.
   */
  previousDepartureGate: z.string().nullable().optional(),
  /**
   * When the producer last saw this state. The freshness bound is the consumer's.
   *
   * `null` is preprocessed to `undefined` so it REJECTS instead of coercing:
   * `z.coerce.date()` turns `null` into `new Date(0)` — a valid-looking
   * 1970-01-01 that no log would flag. Every other field in this block is
   * nullable, so a producer filling the shape writes `null` here by reflex; that
   * is the likely path, not a theoretical one. A clock that lies about its age is
   * worse than an absent one, because the whole point of this field is to let a
   * consumer refuse stale state.
   */
  observedAt: z.preprocess((v) => v ?? undefined, z.coerce.date()),
});
export type FlightLiveStatus = z.infer<typeof FlightLiveStatusSchema>;

/**
 * The WAIT after a hop, at the connection airport. Present only when another hop
 * follows — the last hop of a segment carries `null`.
 *
 * `changeAirport` is the one a traveler must never discover at the gate: some
 * connections land at one airport and depart from another (GRU→CGH in São Paulo),
 * which is a taxi ride, not a walk.
 */
export const FlightLayoverSchema = z.object({
  /** Where the wait happens (source display string, IATA-prefixed). */
  connectionAirport: z.string().nullable(),
  /** True when the next hop departs from a DIFFERENT airport — a transfer, not a walk. */
  changeAirport: z.boolean().nullable(),
  /** True when the next hop is a different aircraft (so, a real connection). */
  changeAirplane: z.boolean().nullable(),
  /** Wait until the next hop, in SECONDS as the source counts it. */
  waitSeconds: z.number().nullable(),
});
export type FlightLayover = z.infer<typeof FlightLayoverSchema>;

/**
 * ONE PHYSICAL FLIGHT inside a segment. A booked leg with a connection is one
 * segment made of two hops: GYN→BSB, then BSB→BEL.
 *
 * VOCABULARY TRAP: the source calls these `stopovers`, which reads like "the
 * stops". They are not stops — they are the FLIGHTS. The stop count lives beside
 * them on the segment (`stops`), and the wait between two hops is the `layover`
 * on the earlier one. A two-hop segment has `stops: 1`.
 *
 * `operatedBy` is the codeshare fact: the airline that actually flies the
 * aircraft, when it differs from the one that sold the ticket (`airline`). Null
 * means the source named no separate operator — NOT that it is unknown.
 */
export const FlightHopSchema = z.object({
  departureAirport: z.string().nullable(),
  arrivalAirport: z.string().nullable(),
  departAt: z.coerce.date().nullable(),
  arriveAt: z.coerce.date().nullable(),
  /**
   * Airport-local wall-clock twins, same contract and same reason as the
   * segment's: the source carries no timezone and writes the airport's wall
   * clock framed at Brazil's -03:00, so `toFlightHops` reads BOTH out of it
   * via `flightSourceTimes` — this local twin (the time on the boarding pass)
   * and the true instant kept in `departAt`/`arriveAt` above. Rendering the
   * source value raw would show a traveler a departure time that is not the
   * one on their boarding pass. Null when the airport zone is unknown.
   */
  departLocal: z.string().nullable(),
  arriveLocal: z.string().nullable(),
  /** Source-formatted, and the source is inconsistent — seconds-as-string in prod. */
  duration: z.string().nullable(),
  flightNumber: z.string().nullable(),
  /** The MARKETING airline — who sold the ticket. */
  airline: z.string().nullable(),
  /** The OPERATING airline when the source names a different one (codeshare). */
  operatedBy: z.string().nullable(),
  /** Per-hop cabin: a segment can change cabin between hops. */
  cabinClass: z.string().nullable(),
  layover: FlightLayoverSchema.nullable(),
  /**
   * THIS flight as it is operating — the same three states as the segment's `live`. Attached
   * only on a segment with a connection (#4244): the segment's `live` speaks of the flight the
   * traveler boards first, and the connecting flight's gate and delay live here, read on this
   * hop's own clock (its arrival side is this hop's landing, never silenced).
   */
  live: FlightLiveStatusSchema.nullable().optional(),
});
export type FlightHop = z.infer<typeof FlightHopSchema>;

export const FlightSegmentSchema = z.object({
  /**
   * The RAW source leg token (`OUTWARD` | `FLIGHT_BACK` | `BACK` | ...). It is
   * NOT the leg direction and must never be read as one — see `direction` below.
   */
  type: z.string().nullable(),
  /**
   * WHICH WAY THIS LEG GOES — the canonical direction, resolved at ingest.
   *
   * `type` cannot answer this. Confirmed on prod (2026-08-02): a round trip
   * bought in one go emits TWO separate vouchers, and the RETURN one carries
   * `type: "OUTWARD"` with `tripType: "RETURNING"`. Anything branching on
   * `type === 'OUTWARD'` therefore labels the return leg as the outbound, and a
   * consumer looking for a `BACK` segment finds none and concludes "one-way".
   *
   * `.optional()` on purpose: mirror docs written before this field lack it, so
   * read it through `flightLegDirection()`, which falls back to the old `type`
   * heuristic instead of silently reporting "unknown".
   */
  direction: z.enum(['OUTWARD', 'RETURN']).nullable().optional(),
  locator: z.string().nullable(), // PNR / booking reference
  companyLocator: z.string().nullable(),
  departureAirport: z.string().nullable(), // IATA
  arrivalAirport: z.string().nullable(), // IATA
  departAt: z.coerce.date().nullable(),
  arriveAt: z.coerce.date().nullable(),
  // Display wall-clock at the airport, as an offset-less ISO string
  // (`YYYY-MM-DDTHH:mm:ss`). The producer carries no timezone and writes this
  // very clock framed at Brazil's -03:00, so the mapper reads BOTH out of it at
  // ingest (via `flightSourceTimes`): this local twin, and the true instant
  // `departAt`/`arriveAt` above, which is what grouping and ordering compare.
  // Null when the airport zone is unknown — callers fall back to a UTC render.
  departLocal: z.string().nullable().optional(),
  arriveLocal: z.string().nullable().optional(),
  duration: z.string().nullable(), // source-formatted (e.g. "PT3H30M" / "3h 30m") — NOT minutes
  airline: z.string().nullable(),
  flightNumbers: z.array(z.string()),
  stops: z.number().nullable(),
  /**
   * The physical flights this segment is made of — see {@link FlightHopSchema}.
   * A direct segment has ONE hop; `stops: 1` means two.
   *
   * `.optional()` on purpose: mirror docs written before this field lack it, and
   * an absent list means "the source detail was never carried", NOT "this flight
   * is direct" — a consumer must fall back to `stops` rather than infer non-stop
   * from an empty array.
   */
  hops: z.array(FlightHopSchema).optional(),
  /**
   * The CABIN (`ECONOMY` | `ECONOMY_PREMIUM` | `BUSINESS` | ...) — what the
   * traveler actually flies in. It used to hold `fares.class`, which is the
   * one-letter RBD booking code (`"P"`, `"W"`), so every surface showing this
   * field showed a letter. The letter is still carried, as `fareClass`.
   */
  cabinClass: z.string().nullable(),
  /** The one-letter fare/RBD booking code (`"P"`, `"W"`) — not the cabin. */
  fareClass: z.string().nullable().optional(),
  seat: z.string().nullable(),
  baggage: z.string().nullable(),
  checkedBaggage: z.boolean().nullable(),
  /**
   * The fare's declared ALLOWANCE (source `fares.tariffDetails[]`), checked and
   * carry-on apart — see {@link BaggageAllowanceSchema}. `baggage`/
   * `checkedBaggage` above are what the source gave BEFORE: a number that mixes
   * carry-on with checked, and a boolean — which is why no surface ever showed
   * pieces or weight (issue #3305).
   *
   * `.optional()` on purpose: mirror docs written before this field lack it, and
   * absent means "the detail was never carried", NOT "no baggage" — a reader
   * falls back to the boolean. It disappears as the re-backfill reprojects.
   */
  baggageAllowance: BaggageAllowanceSchema.nullable().optional(),
  /**
   * The fare's RULES (source `fares.tariffDetails[]`, every line but baggage):
   * change, cancellation, refund, no-show, anticipation and seat selection, each
   * with its status, the supplier's prose verbatim and the R$ amount when the
   * prose writes one — see {@link FareRuleSchema} (#4644). `[]` = the fare
   * declared none of them.
   *
   * `.optional()` on purpose: mirror docs written before this field lack it, and
   * absent means "the rules were never carried", NOT "the fare has no rules".
   */
  fareRules: z.array(FareRuleSchema).optional(),
  /**
   * When the support team cancelled THIS leg alone with the airline, after the
   * voucher was issued (#6394) — the outbound of a round trip sold in one
   * voucher, the return still flying. Not from the legacy source (it has no
   * per-leg state): customer-api attaches it on read from its own
   * `customer_voucher_leg_cancellations`. Read through `isCancelledFlightLeg`.
   *
   * `.optional()` on purpose: absent means "nobody marked this leg", and an
   * unmarked leg is ACTIVE — never infer a cancellation from a missing field.
   */
  cancelledAt: z.coerce.date().nullable().optional(),
  // The flight as it is OPERATING, from a different producer — see
  // FlightLiveStatusSchema. `.optional()` and not `.nullable()`-only on purpose:
  // absent means "nobody attached live state to this read" (the store is off, or
  // this deploy has no monitoring), while an explicit `null` means "monitored,
  // nothing observed yet". Producers that know neither must omit the key.
  live: FlightLiveStatusSchema.nullable().optional(),
});
export type FlightSegment = z.infer<typeof FlightSegmentSchema>;

/**
 * WHICH WAY A FLIGHT LEG GOES — the ONE place that decides it. Every surface that
 * labels a leg (trip panel, approvals card, Wallet pass) must go through here
 * instead of reading `type`.
 *
 * Prefers the ingest-resolved `direction`. Falls back to the raw `type` token for
 * mirror docs written before that field existed — deliberately reproducing the
 * OLD behaviour for them (no worse than today) rather than reporting "unknown"
 * and blanking a label that used to render. The fallback disappears on its own as
 * the re-backfill reprojects those docs.
 *
 * Returns `null` when nothing says which way it goes — callers must NOT default a
 * null to "outbound"; that is the very bug this exists to stop.
 */
export function flightLegDirection(
  segment: Pick<FlightSegment, 'type' | 'direction'>,
): 'OUTWARD' | 'RETURN' | null {
  if (segment.direction != null) return segment.direction;
  const raw = segment.type?.toUpperCase();
  if (!raw) return null;
  if (raw === 'OUTWARD') return 'OUTWARD';
  if (raw === 'BACK' || raw === 'FLIGHT_BACK') return 'RETURN';
  return null;
}

/** The airline PNR shape: exactly 6 uppercase alphanumerics. */
const PNR_SHAPE = /^[A-Z0-9]{6}$/;

/**
 * The airline-facing booking code for a flight segment — what the traveler
 * hands to the counter. Both candidates usually carry the same 6-char PNR, but
 * NDC/consolidator bookings put a long internal code in `locator` and keep the
 * airline PNR in `companyLocator` (confirmed on prod voucher VOLL-F16958497,
 * issue #2090). Shape decides, not field trust: the candidate matching the PNR
 * shape wins; on a tie or when neither matches, `companyLocator` — the
 * third-party code by definition. Never null while any candidate has a value.
 */
export function airlineLocator(segment: {
  locator?: string | null;
  companyLocator?: string | null;
}): string | null {
  const company = segment.companyLocator?.trim() || null;
  const internal = segment.locator?.trim() || null;
  if (company != null && internal != null && PNR_SHAPE.test(internal) && !PNR_SHAPE.test(company)) {
    return internal;
  }
  return company ?? internal;
}

export const FlightDetailsSchema = z.object({
  kind: z.literal('FLIGHT'),
  // One per BOOKED leg (outbound, return) — a connection is NOT a sibling segment,
  // it lives inside the segment's `hops`.
  segments: z.array(FlightSegmentSchema),
});

export const HotelDetailsSchema = z.object({
  kind: z.literal('HOTEL'),
  name: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  checkIn: z.coerce.date().nullable(),
  checkOut: z.coerce.date().nullable(),
  checkInHour: z.string().nullable(),
  checkOutHour: z.string().nullable(),
  guests: z.number().nullable(),
  rooms: z.number().nullable(),
  nights: z.number().nullable(),
  roomDescription: z.string().nullable(),
  cancellationPolicy: z
    .object({
      startAt: z.coerce.date().nullable(),
      endAt: z.coerce.date().nullable(),
      value: z.number().nullable(),
    })
    .nullable(),
  /**
   * Whether the booked RATE is refundable (source `hotel.isRefundable`, #4644) —
   * the yes/no the traveler asks before `cancellationPolicy` says until when and
   * for how much. `null` = the source does not say (concierge stay included);
   * absent = the doc was projected before this field.
   */
  refundable: z.boolean().nullable().optional(),
  /**
   * Where the booked hotel stands. Comes from the legacy voucher's
   * `hotel.geoLocation`, the hotel's OWN point copied from the supplier cache
   * at booking (NOT `selectedPlace.geoLocation`, which is the place the
   * traveler searched around). `null` = the source has no position (hotel
   * outside the cache, manual hotel, concierge stay); absent = the detail was
   * never carried (docs projected before this field), and a reader treats
   * both the same. It disappears as the re-backfill reprojects.
   */
  coordinates: z
    .object({
      latitude: z.number().min(-90).max(90),
      longitude: z.number().min(-180).max(180),
    })
    .nullable()
    .optional(),
});

export const BusSegmentSchema = z.object({
  type: z.string().nullable(), // OUTWARD | BACK (concierge legs) — null for online bookings
  locator: z.string().nullable(), // reservation / booking reference
  departureStation: z.string().nullable(),
  departureAddress: z.string().nullable(),
  arrivalStation: z.string().nullable(),
  arrivalAddress: z.string().nullable(),
  departAt: z.coerce.date().nullable(),
  arriveAt: z.coerce.date().nullable(),
  seat: z.string().nullable(),
  seatClass: z.string().nullable(), // seat/service class description
  company: z.string().nullable(), // bus operator
});
export type BusSegment = z.infer<typeof BusSegmentSchema>;

/**
 * Whether and until when an online bus booking can be cancelled, and for how
 * much (source `bus.allowCanceling` / `travelCancellationFee` /
 * `travelCancellationLimitDate`, #4644). Each field is `null` when the source
 * does not say it in a readable form — never a guess.
 */
export const BusCancellationSchema = z.object({
  allowed: z.boolean().nullable(),
  /** The cancellation fee, in the voucher's currency. `0` = free. */
  fee: z.number().nullable(),
  /** The last instant the booking can still be cancelled. */
  limitAt: z.coerce.date().nullable(),
});
export type BusCancellation = z.infer<typeof BusCancellationSchema>;

export const BusDetailsSchema = z.object({
  kind: z.literal('BUS'),
  segments: z.array(BusSegmentSchema), // outbound / return
  // `null` = an offline (concierge) trip, which carries none of it; absent = the
  // doc was projected before this field.
  cancellation: BusCancellationSchema.nullable().optional(),
});

export const RentalAgencySchema = z.object({
  name: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
});
export type RentalAgency = z.infer<typeof RentalAgencySchema>;

export const RentalDetailsSchema = z.object({
  kind: z.literal('RENTAL'),
  pickupAgency: RentalAgencySchema.nullable(),
  dropoffAgency: RentalAgencySchema.nullable(), // falls back to pickup when the source omits it
  carModel: z.string().nullable(),
  carGroup: z.string().nullable(), // rental category (e.g. "Grupo J")
  // #6004 — the rental company's code ("LL", "MO", "UN": rental-car-api `rental.id`), the key
  // of the kit's logo bank. Optional: a doc mirrored before it still parses.
  companyCode: z.string().nullable().optional(),
  // #5032 — optional: a doc mirrored before these fields still parses.
  transmission: z.enum(['AUTOMATIC', 'MANUAL']).nullable().optional(),
  airConditioning: z.boolean().nullable().optional(),
  includedExtras: z.array(z.string()).optional(), // e.g. DAMAGE_PROTECTION_INCLUDED
  dailyRate: z.number().nullable().optional(),
  days: z.number().int().nullable().optional(), // number of daily rates
});

/** One ride stop: the place the traveler picked ("Sheraton São Paulo") and its
 *  city/state ("São Paulo, SP"). The source has no street line. */
export const MobilityStopSchema = z.object({
  name: z.string(),
  area: z.string().nullable(),
});
export type MobilityStop = z.infer<typeof MobilityStopSchema>;

/** The driver of a scheduled ride (#5030). Only a named driver exists: the legacy app
 *  shows the block only when the name is there. */
export const MobilityDriverSchema = z.object({
  name: z.string(),
  phone: z.string().nullable(),
  car: z.string().nullable(),
  plate: z.string().nullable(),
});
export type MobilityDriver = z.infer<typeof MobilityDriverSchema>;

export const MobilityDetailsSchema = z.object({
  kind: z.literal('MOBILITY'),
  category: z.string().nullable(), // ride category (e.g. "Comfort")
  distance: z.number().nullable(), // km
  duration: z.string().nullable(), // source-formatted, like FlightSegment.duration
  // `.optional()` on the fields below (#5029): mirror docs projected before them lack them.
  origin: MobilityStopSchema.nullable().optional(),
  destination: MobilityStopSchema.nullable().optional(),
  // The price quoted at request time, in the voucher currency; one expected value
  // arrives as min === max.
  estimate: z.object({ min: z.number(), max: z.number() }).nullable().optional(),
  // The receipt the traveler attached to the ride.
  receiptUrl: z.string().nullable().optional(),
  // How many ride categories the request presented.
  optionsCount: z.number().int().nullable().optional(),
  // How the ride was booked, raw from the source (`MOBILITY_SCHEDULE` = a scheduled
  // ride with a driver; the others = an on-demand ride).
  provider: z.string().nullable().optional(),
  // The SCHEDULED ride only (#5030). The schedule kind, raw (`TAXI` | `CAR_DRIVER` |
  // `TRANSFER`; `CAR_DRIVER` = a driver hired by the day).
  scheduleType: z.string().nullable().optional(),
  // The driver the cooperative assigned when it confirmed the ride; `null` before
  // that. `car` = "Toyota Corolla preto" (maker, model and color as the source has them).
  driver: MobilityDriverSchema.nullable().optional(),
  // When the cooperative confirmed the ride (its CONFIRMED step).
  confirmedAt: z.coerce.date().nullable().optional(),
  // The stops between origin and destination, in order.
  stopOvers: z.array(z.string()).optional(),
  // Why the final price differs from the agreed one, as the cooperative wrote it.
  priceNote: z.string().nullable().optional(),
  // The CAR_DRIVER schedule only (#6876): days, package and extras.
  carDriver: MobilityCarDriverSchema.nullable().optional(),
});

/**
 * An extra field the company requires on the expense (Odômetro, Placa… — up to 3), as the
 * traveler filled it (#6229). Only filled fields are kept: `name` and `value` are never blank.
 * The legacy voucher root carries them; `[]` default = a voucher mirrored before the field.
 */
export const AdditionalFieldSchema = z.object({
  id: z.string().nullable(),
  name: z.string(),
  type: z.string().nullable(),
  value: z.string(),
});
export type AdditionalField = z.infer<typeof AdditionalFieldSchema>;

/**
 * A refund the traveler asked for an expense they paid (legacy modal `EXPENSE`,
 * source `expense.*`, #5034). `amount`/`currency` of the voucher are the refund in
 * BRL; `currency` here is what the receipt says, in the currency it was paid.
 * Every field is `null` when the source does not carry it.
 */
export const ExpenseDetailsSchema = z.object({
  kind: z.literal('EXPENSE'),
  /** The company's expense category ("Alimentação") — a free name, not a code. */
  category: z.string().nullable(),
  /** What the traveler wrote about the expense (source `observation`). */
  description: z.string().nullable(),
  /** When the expense happened (source `dateRefund`, else `startDate`). */
  spentAt: z.coerce.date().nullable(),
  /** The receipt's currency: `exchange` is BRL per unit; `tax` is a PERCENT (6 = 6%),
   *  as the legacy app shows it (`ExpenseExchange.taxFormatted`). */
  currency: z
    .object({
      code: z.string().nullable(),
      amount: z.number().nullable(),
      exchange: z.number().nullable(),
      tax: z.number().nullable(),
    })
    .nullable(),
  /** The receipt picture or file the traveler sent. */
  receiptUrl: z.string().nullable(),
  /** The NFC-e (Brazilian consumer invoice) link, when there is one. */
  invoiceUrl: z.string().nullable(),
  /** The wallet the refund goes to (source `wallet.name`). Absent = projected before it. */
  wallet: z.string().nullable().optional(),
  /** How the refund is paid (source `pricing.paymentType`, e.g. `PIX`), raw. */
  paymentType: z.string().nullable(),
  additionalFields: z.array(AdditionalFieldSchema).default([]),
});

/**
 * A mileage refund for the traveler's own car (legacy modal `EXPENSE_KM`, source
 * `expense.*`, #5035). `origin`/`destination` are the free text the traveler typed.
 */
export const ExpenseKmDetailsSchema = z.object({
  kind: z.literal('EXPENSE_KM'),
  origin: z.string().nullable(),
  destination: z.string().nullable(),
  startAt: z.coerce.date().nullable(),
  endAt: z.coerce.date().nullable(),
  distanceKm: z.number().nullable(),
  /** BRL paid per km. */
  valuePerKm: z.number().nullable(),
  /** The traveler changed the computed distance by hand (source `distanceHasChanged`):
   *  `null` unless the source gives both numbers. Absent = projected before the field. */
  distanceChanged: z.object({ originalKm: z.number(), manualKm: z.number() }).nullable().optional(),
  /** The date and the addresses the traveler changed by hand (source
   *  `policyDetails.manualAdjustments`, #6586); the distance has its own `distanceChanged`.
   *  Only whole changes (an original and a final value). Absent = projected before the field. */
  manualChanges: z.array(ExpenseKmManualChangeSchema).default([]),
  /** How the refund is paid (source `pricing.paymentType`, e.g. `PIX`), raw. */
  paymentType: z.string().nullable(),
  /** The GPS numbers of the tracked trip; `null`/absent = no GPS or projected before the field. */
  tracked: ExpenseKmTrackedSchema.nullable().optional(),
  additionalFields: z.array(AdditionalFieldSchema).default([]),
});

/** A payment receipt: the uploaded file (`url`) or the link of an NFC-e read by QR
 *  code (`nfceUrl`). With neither there is nothing to open, and it is not built. */
export const PaymentReceiptSchema = z.object({
  url: z.string().nullable(),
  nfceUrl: z.string().nullable(),
  format: z.string().nullable(), // "pdf", "jpg"…
  date: z.coerce.date().nullable(),
});
export type PaymentReceipt = z.infer<typeof PaymentReceiptSchema>;

/**
 * The payment voucher (#5033): a wallet spend, by pix or card. The VALUE is the
 * voucher's `amount` (the charged one). The raw ids stay here — which one the screen
 * calls "ID da transação" depends on the method (legacy app: pix → `transactionIds[0]`,
 * card → `authorizationCode`). The payee document arrives MASKED: a third party's CPF
 * never enters the mirror.
 */
export const PaymentDetailsSchema = z.object({
  kind: z.literal('PAYMENT'),
  method: z.enum(['PIX', 'CREDIT_CARD']).nullable(),
  wallet: z.string().nullable(), // the wallet spent from ("Alimentação BR")
  // Where the card was used ("Mustang"), from the card purchase of a receipt. The
  // expense CATEGORY is the voucher's `supplier` (`journey.supplier`), as in voucher-api.
  merchant: z.string().nullable(),
  balanceRequestCode: z.string().nullable(), // "REQ-2026-25486"
  transactionIds: z.array(z.string()),
  authorizationCode: z.string().nullable(),
  // The pix destination: payee, masked document, bank; then the key.
  payee: z
    .object({
      name: z.string().nullable(),
      document: z.string().nullable(),
      bank: z.string().nullable(),
    })
    .nullable(),
  pixKeyType: z.enum(['EMAIL', 'CPF', 'CNPJ', 'PHONE', 'EVP', 'BRCODE']).nullable(),
  pixKey: z.string().nullable(),
  receipts: z.array(PaymentReceiptSchema),
  additionalFields: z.array(AdditionalFieldSchema).default([]),
});

export const VoucherDetailsSchema = z.discriminatedUnion('kind', [
  FlightDetailsSchema,
  HotelDetailsSchema,
  BusDetailsSchema,
  RentalDetailsSchema,
  MobilityDetailsSchema,
  ExpenseDetailsSchema,
  ExpenseKmDetailsSchema,
  PaymentDetailsSchema,
]);
export type VoucherDetails = z.infer<typeof VoucherDetailsSchema>;

/**
 * THE TWO PEOPLE ON A VOUCHER. They are different roles and must never be
 * conflated — both are carried as named inline objects precisely so no field
 * name has to be remembered:
 *
 *   - `traveler` — WHO TRAVELS. The passenger, the voucher's owner, our
 *     Customer. Source `traveler.*`.
 *   - `requester` — WHO BOOKED IT. Source `requester.*`.
 *
 * For self-service they are the same person; for concierge/assisted and
 * corporate-events bookings they differ (the requester is then a Voll operator
 * or a company coordinator who may contact support about someone else's trip).
 * The requester is NOT modeled as our Customer (it may live outside the
 * customer mirror), which is why its identity rides inline instead of as a
 * Customer reference.
 *
 * NEITHER carries `externalCode` — that PII lives only on the un-scrubbed
 * mirror-only `TriageBlock` parties and must never cross `scrubVoucher`.
 */
const voucherPartyFields = {
  id: z.string(),
  name: z.string().nullable(),
  email: z.email().nullable(),
};

export const VoucherTravelerSchema = z.object(voucherPartyFields);
export type VoucherTraveler = z.infer<typeof VoucherTravelerSchema>;

export const VoucherRequesterSchema = z.object(voucherPartyFields);
export type VoucherRequester = z.infer<typeof VoucherRequesterSchema>;

/**
 * WHO ELSE TRAVELS — the people sharing the traveler's booking (a second guest
 * in the room, a colleague on the same reservation). A THIRD concept, not a
 * variant of the other two: a companion is neither the voucher's owner nor its
 * booker, so it is carried as its own list rather than nested under `traveler`.
 *
 * The source hangs them off `traveler.companions`, which is why they are easy to
 * mistake for traveler detail. They are people. Absent/empty is `[]`, never null —
 * "travelling alone" is an empty list, not unknown.
 */
export const VoucherCompanionSchema = z.object(voucherPartyFields);
export type VoucherCompanion = z.infer<typeof VoucherCompanionSchema>;

/**
 * WHO MUST DECIDE a voucher awaiting approval (#4459) — the person the traveler
 * goes after when the trip is close. Not a party to the booking, so NOT the party
 * shape: it carries the NAME and nothing else. The approver's id, email and
 * `externalCode` stay on the mirror-only approver chain (ACL-01); the traveler
 * needs to know whom to ask, not how to reach that person through our data.
 */
export const VoucherPendingApproverSchema = z.object({ name: z.string() });

/**
 * ONE company-policy rule this booking broke (#4644), in the three languages the
 * source writes it, so the traveler can read WHY the voucher is out of policy —
 * `within` alone says only that it is. The text is the company's rule plus this
 * trip's own numbers ("…com no mínimo 20 dias de antecedência… resultando em 11
 * dias"); it names no other person. Rules the booking met, or that did not
 * apply, are not carried.
 */
const PolicyRuleTextSchema = z.object({ name: z.string(), description: z.string() });
export const VoucherPolicyBreachSchema = z.object({
  pt: PolicyRuleTextSchema,
  es: PolicyRuleTextSchema,
  en: PolicyRuleTextSchema,
});
export type VoucherPolicyBreach = z.infer<typeof VoucherPolicyBreachSchema>;
export type VoucherPendingApprover = z.infer<typeof VoucherPendingApproverSchema>;

/**
 * ONE approval already given (#5025) — the name and the moment, as the source
 * writes them in `actions.approved[]`. Like {@link VoucherPendingApproverSchema},
 * the approver's email stays on the source: the traveler reads who approved,
 * not how to reach them.
 */
export const VoucherApprovalSchema = z.object({
  name: z.string(),
  at: z.coerce.date().nullable(),
});
export type VoucherApproval = z.infer<typeof VoucherApprovalSchema>;

export const VoucherViewSchema = z.object({
  id: z.string(),
  voucherCode: z.string().nullable(),
  modal: z.string().nullable(),
  status: VoucherStatusSchema.nullable(), // canonical, traveler-facing
  internalStatus: z.string().nullable().optional(), // raw source state-machine value (agents/IA)
  traveler: VoucherTravelerSchema.nullable(), // who travels (our Customer)
  requester: VoucherRequesterSchema.nullable().optional(), // who booked it (may not be a Customer)
  companions: z.array(VoucherCompanionSchema).default([]), // who else travels on this booking
  companyId: z.string().nullable(),
  journey: VoucherJourneyViewSchema.nullable(),
  supplier: z.string().nullable(),
  amount: z.number().nullable(),
  currency: z.string().nullable(),
  emittedAt: z.coerce.date().nullable().optional(), // when issued (EMITTED)
  // Até quando a tarifa do pedido vale (fonte `expiration`, #5025): depois disso o
  // valor pode mudar. Só a UI decide em que estado mostrar. `.optional()` como
  // `within`: docs do espelho anteriores ao backfill v19 não o têm.
  expiresAt: z.coerce.date().nullable().optional(),
  // A parte de taxas e encargos do `amount` (fonte `pricing.fee`, #5025) — a tarifa
  // é `amount − feesAmount`. Na mesma moeda do `amount`. `null` quando a fonte não
  // separa; `.optional()` como `expiresAt`.
  feesAmount: z.number().nullable().optional(),
  reservationNumber: z.string().nullable().optional(), // supplier confirmation number
  details: VoucherDetailsSchema.nullable().optional(), // per-modal operational detail
  // The deterministic voucher join key trip-api's reconcile() matches on (JOIN-01/C8).
  // `.optional()` on purpose: existing mirror docs lack it until the Phase-2 re-backfill
  // completes, so the read API must not 500 on a doc predating the field. Currently only
  // populated for MOBILITY vouchers — `mobility-ride-schedule-admin`'s `schedule.id`
  // (round-trip: the OUTWARD schedule's id; the return leg's id lives at
  // `schedule.back.correlationId` and is not separately carried here). Lets trip-api's
  // exact reconciliation (Task 10) match a booked schedule to its voucher without fuzzy
  // locator/date matching.
  correlationId: z.string().nullable().optional(),
  // A seat or bag extra (TRAVEL_SEAT / TRAVEL_LUGGAGE) names the flight voucher it was
  // bought for: that voucher's `id`. trip-api puts the extra in the trip that carries
  // it. `null` on every other modal; `.optional()` like `within`.
  parentVoucherId: z.string().nullable().optional(),
  // Aggregate policy-compliance flag (the ONLY rich addition to the generic view —
  // the full triage block lives on the separate PendingApprovalTriageView). Enables
  // basic in/out-of-policy labelling for the traveler history in the approval card.
  // `.optional()` on purpose (correlationId precedent): existing mirror docs lack it
  // until the Phase-1 re-backfill completes, so the read API must not 500 on a doc
  // predating the field.
  within: z.boolean().nullable().optional(),
  // WHY it is out of policy — the rules with status WITHOUT, see
  // VoucherPolicyBreachSchema (#4644). `[]` when it broke none; absent on a doc
  // projected before this field.
  policyBreaches: z.array(VoucherPolicyBreachSchema).optional(),
  // O viajante pediu o cancelamento de um voucher EMITIDO (fonte legada
  // `actions.requestCancellation.date`). O `status` segue CONFIRMED: a reserva
  // continua viva no fornecedor até o legado virar CANCELED, que limpa este campo.
  // Só a UI decide o que mostrar (pill "Cancelamento solicitado"). `.optional()`
  // como `within`: docs do espelho anteriores ao backfill v13 não o têm.
  cancellationRequestedAt: z.coerce.date().nullable().optional(),
  // O cancelamento EFETIVO (fonte `actions.canceled`, #6787): quem, quando e por
  // quê — só o nome. Presente apenas com `status: 'CANCELLED'`; nunca serve para
  // inferir o status. `.optional()` como `within`: docs do espelho anteriores ao
  // backfill v34 não o têm.
  cancellation: VoucherCancellationSchema.nullable().optional(),
  // As marcas que o legado põe no voucher (fonte `tags`), cruas: `NOT_USE`,
  // `EVENT`, `REQUESTED_CHANGE`, `RESERVATION_EXTENSION`, `RESERVATION_RETROACTIVE`
  // (#5022). Lista aberta — o rótulo é decisão da UI. `.optional()` como
  // `within`: ausente = sem marca (doc do espelho anterior ao backfill v18).
  tags: z.array(z.string()).optional(),
  // Quem precisa decidir o voucher EM APROVAÇÃO (#4459): só nomes. Presente apenas
  // com `status: 'AWAITING_APPROVAL'` — `[]` é "em aprovação sem aprovador nomeado
  // na fonte". Ausente em qualquer outro status e em produtor anterior ao campo.
  pendingApprovers: z.array(VoucherPendingApproverSchema).optional(),
  // Quem JÁ aprovou e quando (fonte `actions.approved[]`, #5025), na ordem da fonte.
  // `[]` = ninguém aprovou; `.optional()` como `expiresAt`.
  approvals: z.array(VoucherApprovalSchema).optional(),
  // O que o viajante lê nas telas do grupo Solicitação (#5249), tirado no READ do bloco
  // de triagem do espelho (como os aprovadores pendentes): o contexto do pedido (motivo,
  // justificativa, observação, rateio — sem o código contábil), a política aplicada com
  // TODAS as regras, e as opções que a cotação apresentou. Ausentes quando o voucher não
  // passou por avaliação de política; `quotation: null` quando não houve cotação.
  requestContext: ApprovalRequestContextSchema.optional(),
  policy: VoucherPolicySchema.optional(),
  quotation: VoucherQuotationSchema.nullable().optional(),
  // O voucher entrou na composição da viagem SEM vínculo a uma order — anexado
  // pelo fail-safe de conferência do trip-api (#2252: emitido do mesmo viajante
  // na janela, órfão da reconciliação). O cliente pode distingui-lo do serviço
  // vinculado (badge de conferência); ausente = vinculado normal. `.optional()`
  // e producer-only-when-true, como `needsReview` na TripView.
  unlinked: z.boolean().optional(),
  createdAt: z.coerce.date(),
});
export type VoucherView = z.infer<typeof VoucherViewSchema>;

export const CompanyViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.coerce.date(),
  // Moeda (ISO 4217) e país (`code` ISO 3166-1 alpha-2) da empresa, como a origem
  // os tem: `null` = a origem não preencheu; `.optional()` = produtor anterior ao
  // campo. Quem consome valida o formato antes de usar.
  currency: z.string().nullable().optional(),
  country: z
    .object({ name: z.string().nullable(), code: z.string().nullable() })
    .nullable()
    .optional(),
});
export type CompanyView = z.infer<typeof CompanyViewSchema>;
